import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { MagneticButton } from '@/components/lib/ui/MagneticButton';

const trustChips = [
  'Strategy-first, not template-first',
  'Built for consultants, small firms, and micro-agencies',
  'Clear process from first call to launch',
];

export default function ServicesFinalCTASection() {
  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' as const },
    },
  };

  const handleScrollToPackages = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const packagesSection = document.getElementById('packages');
    if (packagesSection) {
      packagesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Gradient Divider */}
      <div className="w-full h-8 bg-gradient-to-b from-white via-blue-500 to-[#0000FF]" />

      {/* Main CTA Section */}
      <section className="relative w-full bg-[#0000FF] text-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Headline */}
              <motion.h2
                variants={itemVariants}
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6 text-center"
              >
                Turn your domain into a client-getting system
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                variants={itemVariants}
                className="text-lg md:text-xl text-blue-50/90 leading-relaxed mb-8 text-center max-w-2xl mx-auto"
              >
                If your domain is just "sitting there," let's change that. We'll map out a plan for your brand, website, and simple systems — so it actually helps bring in better clients.
              </motion.p>

              {/* Primary CTA Button */}
              <motion.div variants={itemVariants} className="flex justify-center mb-4">
                <Link to="/services/consult">
                  <MagneticButton className="w-full max-w-sm">
                    Book my free 1-hour consult
                  </MagneticButton>
                </Link>
              </motion.div>

              {/* Micro-copy */}
              <motion.p
                variants={itemVariants}
                className="text-sm text-blue-50/80 text-center mb-6"
              >
                I'll review your domain, your situation, and your options. No pressure, no hard pitch.
              </motion.p>

              {/* Secondary Link */}
              <motion.div variants={itemVariants} className="text-center mb-8">
                <a
                  href="#packages"
                  onClick={handleScrollToPackages}
                  className="text-blue-50/80 hover:text-white hover:underline text-sm md:text-base transition-colors duration-200"
                >
                  Prefer to review the packages again? See all services →
                </a>
              </motion.div>

              {/* Trust Chips */}
              <motion.div
                variants={itemVariants}
                className="flex flex-wrap justify-center gap-3 pt-4"
              >
                {trustChips.map((chip, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 text-xs text-blue-50/80 bg-white/10 rounded-full"
                  >
                    {chip}
                  </span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

